import { create } from 'zustand'
import { userApi } from '../services'
import { isDevelopmentMode, DEFAULT_MOCK_USER } from '../utils/mockData'
import apiService from '../services/api'

const SUPPORTED_LANGUAGES = ['ru', 'en', 'uk', 'pt', 'es', 'tr', 'kk', 'uz', 'id']

const normalizeLanguageCode = (languageCode) => {
  if (typeof languageCode !== 'string' || !languageCode.trim()) {
    return null
  }

  const normalizedLanguageCode = languageCode.toLowerCase().replace('_', '-')

  if (SUPPORTED_LANGUAGES.includes(normalizedLanguageCode)) {
    return normalizedLanguageCode
  }

  const baseLanguageCode = normalizedLanguageCode.split('-')[0]
  return SUPPORTED_LANGUAGES.includes(baseLanguageCode) ? baseLanguageCode : null
}

const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return null
  }

  const browserLanguages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language]

  for (const browserLanguage of browserLanguages) {
    const normalizedLanguage = normalizeLanguageCode(browserLanguage)

    if (normalizedLanguage) {
      return normalizedLanguage
    }
  }

  return null
}

const getPreferredAppLanguage = ({ telegramLanguage = null } = {}) => {
  return normalizeLanguageCode(telegramLanguage) || getBrowserLanguage() || 'ru'
}

const useAppStore = create((set, get) => ({
  // User data
  user: null,
  balance: 0,
  telegramUser: null,

  // Auth state
  isAuthorized: null,
  authMode: null,
  runtimeEnvironment: 'browser',
  
  // App state
  isLoading: false,
  language: getPreferredAppLanguage(),
  isLanguageLoading: false, // Состояние загрузки при смене языка
  
  // Onboarding state - состояние онбординга
  isFirstVisit: null, // null = не проверено, true = первый визит, false = не первый
  
  // Tasks and other data
  tasks: [],
  recentWins: [],
  
  // Actions
  setUser: (user) => set({ user }),
  setBalance: (balance) => set({ balance }),
  updateBalance: (amount) => set((state) => ({ balance: state.balance + amount })),
  setTelegramUser: (telegramUser) => set({ telegramUser }),
  setAuthState: ({ isAuthorized, authMode, runtimeEnvironment = 'browser' }) => set({
    isAuthorized,
    authMode,
    runtimeEnvironment
  }),
  setLanguage: (language) => set({ language }),
  setLanguageLoading: (isLanguageLoading) => set({ isLanguageLoading }),
  setTasks: (tasks) => set({ tasks }),
  setRecentWins: (recentWins) => set({ recentWins }),
  setLoading: (isLoading) => set({ isLoading }),
  
  // Onboarding actions - действия для онбординга
  setIsFirstVisit: (isFirstVisit) => {
    console.log('🎯 setIsFirstVisit called:', isFirstVisit)
    set({ isFirstVisit })
  },
  
  // Initialize app with Telegram data and backend API
  initializeApp: async (i18nChangeLanguage = null) => {
    console.log('🚀 useAppStore.initializeApp() started')
    set({ isLoading: true })
    
    try {
      const webAuthResult = apiService.initializeWebAuthFromUrl()
      const authContext = apiService.getAuthContext()

      get().setAuthState({
        isAuthorized: authContext.isAuthorized,
        authMode: authContext.mode,
        runtimeEnvironment: authContext.runtimeEnvironment
      })

      if (webAuthResult.hasNewAuth) {
        console.log('🌐 Обнаружена новая web-авторизация, пользователь будет открыт с главной страницы')
      }

      if (!authContext.isAuthorized) {
        console.warn('⚠️ Пользователь не авторизован, приложение будет работать в публичном режиме')
        set({
          telegramUser: null,
          user: null,
          isFirstVisit: false,
          runtimeEnvironment: authContext.runtimeEnvironment,
          language: getPreferredAppLanguage()
        })
        return
      }

      if (authContext.mode === 'development' && isDevelopmentMode()) {
        // Use mock data for localhost development
        set({ 
          telegramUser: DEFAULT_MOCK_USER,
          runtimeEnvironment: authContext.runtimeEnvironment,
          language: getPreferredAppLanguage({ telegramLanguage: DEFAULT_MOCK_USER.language_code })
        })
        
        console.log('🔧 Development mode: Using mock Telegram user data', DEFAULT_MOCK_USER)
      } else if (authContext.runtimeEnvironment === 'telegram') {
        // Initialize Telegram WebApp with enhanced validation
        const webAppInitialized = apiService.initializeTelegramWebApp()
        
        if (webAppInitialized && window.Telegram?.WebApp) {
          const tg = window.Telegram.WebApp
          
          const telegramLanguage = getPreferredAppLanguage({
            telegramLanguage: tg.initDataUnsafe?.user?.language_code
          })
          set({ language: telegramLanguage })
        } else {
          console.error('❗ Failed to initialize Telegram WebApp')
          set({ language: getPreferredAppLanguage() })
        }
      } else {
        console.log('🌐 Browser web-auth mode detected')
        set({
          telegramUser: null,
          runtimeEnvironment: authContext.runtimeEnvironment,
          language: getPreferredAppLanguage()
        })
      }
      
      // Загружаем профиль пользователя для проверки is_first_visit и синхронизации языка
      console.log('📡 Загружаем профиль пользователя...')
      try {
        await get().loadProfile(i18nChangeLanguage)
        console.log('✅ Профиль загружен успешно')
      } catch (error) {
        console.error('❌ Ошибка загрузки профиля при инициализации:', error)
        // Устанавливаем значение по умолчанию если API недоступен
        console.log('🔄 Устанавливаем isFirstVisit = false по умолчанию')
        set({ isFirstVisit: false })
      }
      
      // 🧪 ТЕСТИРОВАНИЕ: Раскомментируй нужную строку для тестирования
      // set({ isFirstVisit: true })  // Тестируем онбординг для нового пользователя
      // set({ isFirstVisit: false }) // Тестируем обычный флоу для существующего пользователя
      
    } catch (error) {
      console.error('❌ Failed to initialize app:', error)
    } finally {
      console.log('🏁 useAppStore.initializeApp() finished, isLoading = false')
      set({ isLoading: false })
    }
  },
  
  // Load user profile
  loadProfile: async (i18nChangeLanguage = null) => {
    try {
      // НЕ устанавливаем isLoading: true здесь, чтобы избежать циклических вызовов
      // isLoading управляется только в initializeApp()
      const response = await userApi.getProfile()
      
      if (response.me && response.me.lang) {
        const serverLanguage = normalizeLanguageCode(response.me.lang)
        const { language, setLanguage } = get()
        
        if (serverLanguage && serverLanguage !== language) {
          console.log(`🌐 Обновляем язык с сервера: ${language} → ${serverLanguage}`)
          setLanguage(serverLanguage)
          
          if (i18nChangeLanguage) {
            i18nChangeLanguage(serverLanguage)
          }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Failed to load profile:', error)
      throw error
    }
  },
  
  /**
   * Смена языка пользователя с API вызовом
   * @param {string} newLanguage - Новый язык (ru, en, etc.)
   * @param {Function} i18nChangeLanguage - Функция смены языка i18next
   * @returns {Promise<boolean>} - true если успешно, false если ошибка
   */
  changeLanguage: async (newLanguage, i18nChangeLanguage) => {
    const { language, setLanguageLoading, setLanguage, setUser } = get()
    const previousLanguage = language
    
    try {
      console.log(`🌐 Смена языка с ${previousLanguage} на ${newLanguage}`)
      setLanguageLoading(true)
      
      // Отправляем запрос на сервер для смены языка
      const response = await userApi.updateLanguage(newLanguage)
      
      // Проверяем успешность операции
      if (response.lang && response.lang.result) {
        // Обновляем язык в store только после успешного ответа сервера
        setLanguage(newLanguage)
        
        // Обновляем данные пользователя из ответа сервера
        if (response.me) {
          setUser(response.me)
          console.log('✅ Данные пользователя обновлены после смены языка')
        }
        
        // Меняем язык в i18next только после успешного API вызова
        if (i18nChangeLanguage) {
          i18nChangeLanguage(newLanguage)
        }
        
        console.log(`✅ Язык успешно изменен на ${newLanguage}`)
        return true
      } else {
        throw new Error('Сервер вернул неуспешный результат смены языка')
      }
      
    } catch (error) {
      console.error('❌ Ошибка при смене языка:', error)
      
      // Откатываем язык обратно при ошибке
      console.log(`🔄 Откатываем язык обратно на ${previousLanguage}`)
      setLanguage(previousLanguage)
      
      if (i18nChangeLanguage) {
        i18nChangeLanguage(previousLanguage)
      }
      
      // Пробрасываем ошибку для обработки в компоненте
      throw error
    } finally {
      setLanguageLoading(false)
    }
  },
  
  // Complete a task
  completeTask: async (taskId) => {
    try {
      return await userApi.completeTask(taskId)
    } catch (error) {
      console.error('Failed to complete task:', error)
      throw error
    }
  },
  
  // Update trade URL
  updateTradeUrl: async (tradeUrl) => {
    try {
      await userApi.updateTradeUrl(tradeUrl)
    } catch (error) {
      console.error('Failed to update trade URL:', error)
      throw error
    }
  },

  // Поделиться приглашением через Telegram
  shareInviteMessage: async (inviteText, refLink, translations = {}) => {
    try {
      // Проверяем, действительно ли мы в Telegram WebApp
      const isInTelegram = window.Telegram?.WebApp?.initData && 
                          window.Telegram.WebApp.initData.length > 0 &&
                          !isDevelopmentMode()
      
      if (isInTelegram && window.Telegram.WebApp.shareMessage) {
        try {
          // Создаем PreparedInlineMessage через API
          const response = await userApi.createPreparedInviteMessage(inviteText, refLink)
          
          if (response.prepared_message?.id) {
            // Используем нативное окно выбора получателя
            window.Telegram.WebApp.shareMessage(response.prepared_message.id, (success) => {
              if (success) {
                console.log('✅ Приглашение успешно отправлено!')
              } else {
                console.log('❌ Пользователь отменил отправку приглашения')
              }
            })
          } else {
            throw new Error(translations.createMessageError || 'Failed to create message for sending')
          }
        } catch (apiError) {
          console.error('Ошибка создания PreparedInlineMessage:', apiError)
          // Fallback к popup с копированием
          get().showCopyPopup(inviteText, refLink, translations)
        }
      } else {
        // Fallback для старых версий Telegram или разработки
        get().showCopyPopup(inviteText, refLink, translations)
      }
    } catch (error) {
      console.error('Ошибка при отправке приглашения:', error)
      throw error
    }
  },

  // Fallback метод для показа popup с копированием
  showCopyPopup: (inviteText, refLink, translations = {}) => {
    const fullMessage = `${inviteText}\n\n${refLink}`
    
    if (window.Telegram?.WebApp?.showPopup) {
      // В Telegram WebApp показываем popup
      window.Telegram.WebApp.showPopup({
        title: translations.shareTitle || 'Поделиться приглашением',
        message: translations.shareMessage || 'Скопируйте текст и отправьте друзьям в любом чате:',
        buttons: [
          {id: 'copy', type: 'default', text: translations.copy || 'Копировать'},
          {id: 'close', type: 'cancel', text: translations.close || 'Закрыть'}
        ]
      }, (buttonId) => {
        if (buttonId === 'copy' && navigator.clipboard) {
          navigator.clipboard.writeText(fullMessage)
          window.Telegram.WebApp.showAlert(translations.copySuccess || 'Сообщение скопировано! Теперь отправьте его друзьям.')
        }
      })
    } else {
      // Fallback для разработки или браузера
      console.log('🔧 Development/Browser mode: Simulating invitation sending')
      console.log('Текст сообщения:', fullMessage)
      
      // Копируем в буфер обмена для удобства тестирования
      if (navigator.clipboard) {
        navigator.clipboard.writeText(fullMessage)
        console.log('✅ Message copied to clipboard')
        alert((translations.copySuccessBrowser || 'Сообщение скопировано в буфер обмена!') + '\n\n' + fullMessage)
      } else {
        // Если clipboard API недоступен, показываем текст
        alert((translations.copyManual || 'Скопируйте и отправьте это сообщение друзьям:') + '\n\n' + fullMessage)
      }
    }
  }
}))

export default useAppStore
